/**
 * Refine Auth Provider
 * Implements Refine's AuthProvider interface with Zustand store integration
 *
 * Phase 2, Session 01: Base implementation with placeholder login
 * Sessions 02-03 will add: magic link verification, Google OAuth
 */

import type { AuthProvider } from "@refinedev/core";
import { useAuthStore } from "@/stores/auth";
import type { AuthUser, AuthLoginParams } from "@/types";

/**
 * Get current auth state from Zustand store
 * Uses getState() for access outside React components
 */
const getAuthState = () => useAuthStore.getState();

/**
 * Refine authProvider implementation
 * Delegates state management to Zustand store with localStorage persistence
 */
export const authProvider: AuthProvider = {
  /**
   * Login handler
   * Phase 2 Session 01: Placeholder that accepts any email
   * Phase 2 Sessions 02-03: Will validate magic link tokens and Google OAuth
   *
   * @param params - Login credentials with email
   * @returns Success result with redirect
   */
  login: async (params: AuthLoginParams) => {
    const { email } = params;

    if (!email) {
      return {
        success: false,
        error: {
          name: "LoginError",
          message: "Email is required",
        },
      };
    }

    // Placeholder: Create a mock user session
    // Real implementation in Sessions 02-03 will:
    // - Session 02: Verify magic link token against Airtable
    // - Session 03: Verify Google OAuth token
    const mockUser: AuthUser = {
      id: `user-${Date.now()}`,
      email,
      name: email.split("@")[0],
      accountStatus: "active",
      creditBalance: 0,
    };

    const { setUser, setLoading } = getAuthState();
    setLoading(true);

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    setUser(mockUser);
    setLoading(false);

    return {
      success: true,
      redirectTo: "/voice-ai-coaching",
    };
  },

  /**
   * Logout handler
   * Clears session from Zustand store and localStorage
   *
   * @returns Success result with redirect to login
   */
  logout: async () => {
    const { clearAuth, setLoading } = getAuthState();

    setLoading(true);
    clearAuth();
    setLoading(false);

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  /**
   * Auth check
   * Validates if user has an active session
   * Waits for hydration if store is still loading from localStorage
   *
   * @returns Authentication status with optional redirect
   */
  check: async () => {
    const state = getAuthState();

    // Wait for hydration if not complete
    if (!state.isHydrated) {
      // Give hydration a moment to complete
      await new Promise((resolve) => setTimeout(resolve, 100));
      const newState = getAuthState();

      if (newState.user) {
        return { authenticated: true };
      }

      return {
        authenticated: false,
        redirectTo: "/login",
        error: {
          name: "AuthError",
          message: "Session not found",
        },
      };
    }

    // Check if user exists in session
    if (state.user) {
      return { authenticated: true };
    }

    return {
      authenticated: false,
      redirectTo: "/login",
      error: {
        name: "AuthError",
        message: "Not authenticated",
      },
    };
  },

  /**
   * Get user identity
   * Returns current user from Zustand store
   *
   * @returns User object or null
   */
  getIdentity: async () => {
    const { user, isHydrated } = getAuthState();

    // Wait for hydration if not complete
    if (!isHydrated) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      return getAuthState().user;
    }

    return user;
  },

  /**
   * Error handler
   * Handles auth-related errors from API calls
   * Redirects to login on 401 unauthorized
   *
   * @param error - The error from API call
   * @returns Error response with optional logout
   */
  onError: async (error) => {
    const status = (error as { statusCode?: number })?.statusCode;

    if (status === 401 || status === 403) {
      const { clearAuth } = getAuthState();
      clearAuth();

      return {
        logout: true,
        redirectTo: "/login",
        error,
      };
    }

    return { error };
  },

  /**
   * Get permissions
   * Returns user permissions/roles
   * Currently returns empty array - can be extended for RBAC
   *
   * @returns Array of permission strings
   */
  getPermissions: async () => {
    const { user } = getAuthState();

    if (!user) {
      return [];
    }

    // Can be extended to return role-based permissions
    // e.g., ["user", "premium"] based on user.accountStatus
    return [];
  },

  /**
   * Register handler (optional)
   * Handles new user registration
   * Placeholder implementation - real signup in Session 02
   *
   * @param params - Registration data with email
   * @returns Success result
   */
  register: async (params: { email: string; name?: string }) => {
    const { email } = params;

    if (!email) {
      return {
        success: false,
        error: {
          name: "RegisterError",
          message: "Email is required",
        },
      };
    }

    // Placeholder: In Session 02, this will:
    // 1. Create/find user in Airtable
    // 2. Trigger magic link email via Make.com
    // 3. Return pending state

    return {
      success: true,
      // User stays on sign-up page with success notification
      successNotification: {
        message: "Check your email for a login link",
        description: "We sent a magic link to " + email,
      },
    };
  },

  /**
   * Forgot password handler (optional)
   * Not used - app uses passwordless auth only
   */
  forgotPassword: async () => {
    return {
      success: false,
      error: {
        name: "NotImplemented",
        message: "This app uses passwordless authentication",
      },
    };
  },

  /**
   * Update password handler (optional)
   * Not used - app uses passwordless auth only
   */
  updatePassword: async () => {
    return {
      success: false,
      error: {
        name: "NotImplemented",
        message: "This app uses passwordless authentication",
      },
    };
  },
};
